import React, { useState, useEffect, useCallback, useRef } from 'react'
import { shortenSido } from '../../utils/address'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { Place } from '../api/place'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminPlaceDetailModal from '../models/AdminPlaceDetailModal'
import { useCategoryStore } from '../../stores/categoryIconStore'

const AdminPlaces: React.FC = () => {
  const navigate = useNavigate()
  const [places, setPlaces] = useState<Place[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [togglingPlaceId, setTogglingPlaceId] = useState<number | null>(null)

  const [onlyGeneric, setOnlyGeneric] = useState(false)

  const fetchPlaces = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getPlaces({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
        ...(onlyGeneric ? { detail: 'generic' as const, reviewed_only: true } : {}),
      })
      setPlaces(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('장소 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, navigate, onlyGeneric])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchPlaces()
  }, [navigate, fetchPlaces])

  /**
   * 세부 라벨이 대분류에서 멈춘 장소만 골라 한 줄씩 고치는 모드.
   *
   * 카카오가 '음식점 > 한식'까지만 주면 라벨이 '한식'에서 멈춘다. 3단계 이상이면
   * '칼국수'처럼 구체적으로 나오므로 손댈 게 없다. 그 '멈춘 것'만 모아 보여준다.
   * 모달을 열었다 닫는 왕복 없이 줄에서 바로 고치도록 입력칸을 둔다.
   */
  const [draft, setDraft] = useState<Record<number, string>>({})
  const [savingId, setSavingId] = useState<number | null>(null)

  /**
   * 네이버 지도를 '같은 탭'에서 연다.
   *
   * 줄마다 새 창이 뜨면 열고 닫기를 반복해야 한다. window.open에 이름을 주면
   * 그 이름의 탭이 이미 있을 때 새로 만들지 않고 그 탭을 다시 쓴다. 창을 옆에
   * 띄워두고 목록을 내려가면 탭이 따라 바뀐다.
   *
   * 네이버 지도는 X-Frame-Options가 DENY라 화면 안에 넣을 수는 없다.
   */
  const naverTabRef = useRef<Window | null>(null)

  const openNaver = useCallback((place: Place) => {
    const url = `https://map.naver.com/p/search/${encodeURIComponent(
      `${place.name} ${place.address ?? ''}`.trim()
    )}`
    const tab = window.open(url, 'tastee-naver')
    if (tab) {
      naverTabRef.current = tab
      tab.focus()
    }
  }, [])

  /**
   * 입력칸에 들어가면 네이버 탭도 그 가게로 옮겨준다.
   *
   * 단 한 번도 연 적이 없으면 아무것도 하지 않는다. 사용자가 누르지 않았는데
   * 창을 열면 팝업 차단에 걸리고, 원하지도 않은 창이 뜬다.
   */
  const syncNaverTab = useCallback((place: Place) => {
    if (!naverTabRef.current || naverTabRef.current.closed) return
    openNaver(place)
  }, [openNaver])

  const handleSaveSubCategory = async (place: Place) => {
    const value = (draft[place.id] ?? '').trim()
    if (!value) return
    try {
      setSavingId(place.id)
      await adminApi.updatePlace(place.id, {
        name: place.name,
        category: place.category,
        lat: String(place.lat),
        lng: String(place.lng),
        address: place.address ?? '',
        phone_number: place.phone_number ?? '',
        sub_category: value,
      })
      toastUtil.success(`${place.name} → ${value}`)
      // 고친 줄은 더 이상 '멈춘 것'이 아니므로 목록에서 뺀다
      setPlaces((prev) => prev.filter((p) => p.id !== place.id))
      setTotal((t) => Math.max(0, t - 1))
      setDraft((d) => { const n = { ...d }; delete n[place.id]; return n })
    } catch (error: any) {
      toastUtil.error(error.response?.data?.message || '저장에 실패했습니다')
    } finally {
      setSavingId(null)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchPlaces()
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCategory = (category: string) => {
    const placeCategory = useCategoryStore.getState().getCategory(category)
    return placeCategory?.name || category
  }

  const formatSubCategory = (subCategory: string | null) => {
    if (!subCategory) return '-'
    const subCategoryMap: Record<string, string> = {
      korean: '한식',
      chinese: '중식',
      western: '양식',
      japanese: '일식',
      brunch: '브런치',
    }
    return subCategoryMap[subCategory] || subCategory
  }

  const handleViewDetail = (place: Place) => {
    setSelectedPlace(place)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPlace(null)
  }

  const handleUpdatePlace = (updatedPlace: Place) => {
    // 수정된 장소 정보로 업데이트
    setSelectedPlace(updatedPlace)
    // 목록도 새로고침
    fetchPlaces()
  }

  const handleDeletePlaceSuccess = () => {
    handleCloseModal()
    fetchPlaces()
  }

  const handleToggleRecommended = async (place: Place) => {
    try {
      setTogglingPlaceId(place.id)
      const response = await adminApi.updatePlaceRecommended(
        place.id,
        !(place.is_recommended ?? false)
      )
      toastUtil.success(response.message)
      setPlaces((prev) =>
        prev.map((p) =>
          p.id === place.id ? { ...p, is_recommended: response.data.is_recommended } : p
        )
      )
      if (selectedPlace?.id === place.id) {
        setSelectedPlace({ ...selectedPlace, is_recommended: response.data.is_recommended })
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || '선정 여부 수정 중 오류가 발생했습니다.'
      toastUtil.error(errorMessage)
    } finally {
      setTogglingPlaceId(null)
    }
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                장소 관리
              </h2>
              <div className="text-style-body2 text-textColorText2">
                총 {total.toLocaleString()}개
              </div>
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="장소명, 주소, 구글 플레이스 ID로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
                {/*
                  카카오가 '음식점 > 한식'까지만 줘서 라벨이 대분류에서 멈춘 것들.
                  3단계 이상이면 '칼국수'처럼 구체적으로 나오므로 여기 안 걸린다.
                */}
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPage(1)
                    setOnlyGeneric((v) => !v)
                  }}
                  className={`shrink-0 py-12px px-20px rounded-8px text-style-button2 border transition-colors ${
                    onlyGeneric
                      ? 'bg-4B3621-80% text-white border-transparent'
                      : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                  }`}
                >
                  세부분류 정리
                </button>
              </div>
              {onlyGeneric && (
                <p className="text-style-labelS text-textColorText3 mt-8px">
                  카카오 분류가 대분류에서 멈춘 장소만, 기록 많은 순으로 보여줍니다.
                  <b>네이버</b>를 한 번 누르면 창이 열리고, 그다음부터는 입력칸을
                  옮길 때마다 <b>같은 창이 그 가게로 따라옵니다</b>. 적고 Enter를 누르면
                  저장되고 목록에서 사라집니다. 남은 {total}곳.
                </p>
              )}
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : places.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  장소가 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          장소명
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          카테고리
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          주소
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          전화번호
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          선정
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          등록일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {places.map((place) => (
                        <tr
                          key={place.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {place.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {place.name}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {onlyGeneric ? (
                              <div className="flex flex-col gap-4px min-w-260px">
                                <div className="text-style-labelS text-textColorText3">
                                  {place.kakao_category || '-'}
                                  {typeof place.reviews_count === 'number' && (
                                    <span className="ml-8px">기록 {place.reviews_count}</span>
                                  )}
                                </div>
                                <div className="flex gap-6px">
                                  <input
                                    type="text"
                                    value={draft[place.id] ?? ''}
                                    onChange={(e) =>
                                      setDraft((d) => ({ ...d, [place.id]: e.target.value }))
                                    }
                                    onFocus={() => syncNaverTab(place)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleSaveSubCategory(place)
                                      }
                                    }}
                                    placeholder={`지금: ${place.sub_category ?? '-'}`}
                                    className="flex-1 min-w-0 py-6px px-10px border border-brandPrimary-20% rounded-8px text-style-body2 text-textColorText2 bg-transparent focus:outline-none focus:border-brandPrimary placeholder:text-textColorText3"
                                  />
                                  {/* 새 창이라 목록이 날아가지 않는다. 보고 와서 적으면 된다 */}
                                  <button
                                    type="button"
                                    onClick={() => openNaver(place)}
                                    className="shrink-0 px-10px rounded-8px border border-brandPrimary-20% text-style-labelS text-textColorText2 hover:bg-4B3621-80%-5%"
                                  >
                                    네이버
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveSubCategory(place)}
                                    disabled={savingId === place.id || !(draft[place.id] ?? '').trim()}
                                    className="shrink-0 px-10px rounded-8px bg-4B3621-80% text-white text-style-labelS disabled:opacity-40"
                                  >
                                    {savingId === place.id ? '…' : '저장'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {formatCategory(place.category)}
                                {place.sub_category && (
                                  <span className="text-textColorText2 ml-4px">
                                    ({formatSubCategory(place.sub_category)})
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {place.address ? shortenSido(place.address) : '-'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {place.phone_number || '-'}
                          </td>
                          <td className="py-12px px-16px">
                            <label className="flex items-center gap-8px cursor-pointer">
                              <input
                                type="checkbox"
                                checked={place.is_recommended ?? false}
                                onChange={() => handleToggleRecommended(place)}
                                disabled={togglingPlaceId === place.id}
                                className="w-16px h-16px rounded border-brandPrimary-20% text-brown-80%-main focus:ring-brandPrimary"
                              />
                              <span className="text-style-body2 text-textColorText2">
                                {(place.is_recommended ?? false) ? '선정' : '미선정'}
                              </span>
                            </label>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(place.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <button
                              onClick={() => handleViewDetail(place)}
                              className={`
                                py-8px
                                px-16px
                                bg-4B3621-80%
                                text-white
                                rounded-8px
                                text-style-button2
                                hover:bg-4B3621-80%-70%
                                transition-colors
                              `}
                            >
                              상세보기
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="장소 상세 정보"
        maxWidth="max-w-[800px]"
        overlayOpacity="bg-opacity-50"
      >
        {selectedPlace && (
          <AdminPlaceDetailModal
            place={selectedPlace}
            onClose={handleCloseModal}
            formatDate={formatDate}
            formatCategory={formatCategory}
            formatSubCategory={formatSubCategory}
            onUpdate={handleUpdatePlace}
            onDelete={() => {}}
            onDeleteSuccess={handleDeletePlaceSuccess}
          />
        )}
      </AdminBaseModal>
    </div>
  )
}

export default AdminPlaces
